import React, { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  DateField,
  TopToolbar,
  useListController,
  useRefresh,
  useNotify,
  WrapperField,
  useRecordContext,
} from "react-admin";
import {
  Box,
  Chip,
  IconButton,
  Button,
  MenuItem,
  useMediaQuery,
  Tooltip,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  TextField as MuiTextField,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomPagination from "../../Components/CustomPagination";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { dataProvider } from "../../Provider/parseDataProvider";
import EditTicketDialog from "./EditTicketDialog";
import TicketDetailsDialog from "./TicketDetailsDialog";

const categoryChoices = [
  { id: "user_data", name: "User Data" },
  { id: "recharge", name: "Recharge" },
  { id: "redeem", name: "Redeem" },
  { id: "game_coins", name: "Game Coins" },
  { id: "daily_coins", name: "Daily Coins" },
  { id: "account", name: "Account" },
  { id: "reports", name: "Reports" },
  { id: "profile", name: "Profile" },
  { id: "marketing", name: "Marketing" },
  { id: "others", name: "Others" },
];

const statusChoices = [
  { id: "new", name: "New" },
  { id: "in_progress", name: "In Progress" },
  { id: "resolved", name: "Resolved" },
];

const roleChoices = [
  { id: "Distributor", name: "Distributor" },
  { id: "Master-Distributor", name: "Master-Distributor" },
  { id: "Game-Owner", name: "Game-Owner" },
  { id: "Sub-Distributor", name: "Sub-Distributor" },
  { id: "Agent", name: "Agent" },
];

const getStatusColor = (status) => {
  if (!status) return "default";
  switch (status.toLowerCase()) {
    case "new":
      return "primary";
    case "in_progress":
      return "warning";
    case "resolved":
      return "success";
    default:
      return "default";
  }
};

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string
    .split(/[ _]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Actions Button Component
const TicketActions = ({ onEdit, onViewDetails }) => {
  const record = useRecordContext();
  const role = localStorage.getItem("role");

  if (!record) return null;

  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title="View Details">
        <IconButton
          size="small"
          onClick={() => onViewDetails(record)}
          sx={{
            color: "#FFD700",
            "&:hover": {
              bgcolor: "rgba(255, 215, 0, 0.1)",
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {role === "Super-User" && (
        <Tooltip title="Edit Ticket">
          <IconButton
            size="small"
            onClick={() => onEdit(record)}
            sx={{
              color: "#FFD700",
              "&:hover": {
                bgcolor: "rgba(255, 215, 0, 0.1)",
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const TicketsList = (props) => {
  const listContext = useListController(props);
  const {
    filterValues,
    setFilters,
    page,
    perPage,
    total,
    setPage,
    setPerPage,
  } = listContext;

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const refresh = useRefresh();
  const notify = useNotify();

  const selectedCategory = filterValues.category || "";
  const selectedStatus = filterValues.status || "";
  const selectedRole = filterValues.role || "";

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setEditDialogOpen(true);
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setDetailsDialogOpen(true);
  };

  const handleEditSuccess = () => {
    notify("Ticket updated successfully", { type: "success" });
    refresh();
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleDetailsClose = () => {
    setDetailsDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleApply = () => {
    if (!filterValue || filterValue === "") return;
    setFilters({ ...filterValues, searchBy: filterValue });
  };

  const handleClear = () => {
    setFilterValue("");
    setFilters({});
  };

  const handleExport = async () => {
    try {
      const response = await dataProvider.getList("tickets", {
        pagination: { page: 1, perPage: 100000 },
        sort: { field: "createdAt", order: "DESC" },
        filter: filterValues,
      });

      if (!response || !response.data || response.data.length === 0) {
        notify("No data to export", { type: "warning" });
        return;
      }

      const exportData = response.data.map((record) => ({
        Username: record.username,
        Role: record.role || "N/A",
        "Ticket ID": record.ticketId || record.id,
        Category: capitalizeFirstLetter(record.category),
        Status: capitalizeFirstLetter(record.status),
        Description: record.description,
        Remarks: record.remarks || "",
        "Date & Time": new Date(record.createdAt).toLocaleString(),
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Support Tickets");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // Save file
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        data,
        `Support_Tickets_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      notify("Tickets exported successfully", { type: "success" });
    } catch (error) {
      console.error("Error exporting data:", error);
      notify("Failed to export tickets", { type: "error" });
    }
  };

  const postListActions = (
    <TopToolbar
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        width: { xs: "100%", sm: "auto" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "space-between",
          justifyContent: isSmallScreen ? "space-between" : "flex-end",
          gap: 2,
          width: "100%",
        }}
      >
        <Button
          variant="contained"
          startIcon={<GetAppIcon />}
          sx={{
            marginLeft: "10px",
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>
    </TopToolbar>
  );

  // const filters = [
  //   <Box
  //     key="search-filter"
  //     sx={{
  //       display: "flex",
  //       flexDirection: {
  //         sm: "row",
  //       },
  //       "@media (max-width:536px)": {
  //         flexDirection: "column",
  //         alignItems: "flex-end",
  //         gap: 1,
  //       },
  //       alignItems: "end",
  //       justifyContent: "flex-start",
  //       flexWrap: "nowrap",
  //       overflowX: "auto",
  //       gap: 2,
  //       width: "100%",
  //       mb: 0.5,
  //     }}
  //     alwaysOn
  //   >
  //     <MuiTextField
  //       size="small"
  //       variant="outlined"
  //       value={filterValue}
  //       onChange={(e) => setFilterValue(e.target.value)}
  //       placeholder="Username or TicketId"
  //       onKeyDown={(e) => {
  //         if (e.key === "Enter") {
  //           e.preventDefault();
  //           handleApply();
  //         }
  //       }}
  //       sx={{
  //         minWidth: "200px",
  //         "@media (max-width:536px)": {
  //           width: "100%",
  //         },
  //         "& .MuiInputBase-root": {
  //           color: "#FFF",
  //           bgcolor: "rgba(255, 255, 255, 0.05)",
  //           "& fieldset": {
  //             borderColor: "rgba(255, 239, 153, 0.20)",
  //           },
  //           "&:hover fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //           "&.Mui-focused fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //         },
  //         "& .MuiInputBase-input::placeholder": {
  //           color: "rgba(255, 255, 255, 0.5)",
  //         },
  //       }}
  //     />
  //     <Button
  //       variant="contained"
  //       sx={{
  //         background: "#FFC107",
  //         color: "#0A0A0A",
  //         textTransform: "none",
  //         "&:hover": {
  //           backgroundColor: "#FFA000",
  //         },
  //       }}
  //       onClick={handleApply}
  //     >
  //       Search
  //     </Button>
  //     <Button
  //       variant="outlined"
  //       sx={{
  //         border: "1px solid #FFC107",
  //         color: "white",
  //         textTransform: "none",
  //       }}
  //       onClick={handleClear}
  //     >
  //       Clear
  //     </Button>

  //     <FormControl
  //       size="small"
  //       sx={{
  //         minWidth: 150,
  //         "& .MuiInputLabel-root": {
  //           color: "rgba(255, 255, 255, 0.7)",
  //           "&.Mui-focused": {
  //             color: "#FFD700",
  //           },
  //         },
  //         "& .MuiOutlinedInput-root": {
  //           color: "#FFF",
  //           bgcolor: "rgba(255, 255, 255, 0.05)",
  //           "& fieldset": {
  //             borderColor: "rgba(255, 239, 153, 0.20)",
  //           },
  //           "&:hover fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //           "&.Mui-focused fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //         },
  //         "& .MuiSelect-icon": {
  //           color: "#FFD700",
  //         },
  //       }}
  //     >
  //       <InputLabel>Category</InputLabel>
  //       <Select
  //         value={selectedCategory}
  //         label="Category"
  //         onChange={(e) =>
  //           setFilters({ ...filterValues, category: e.target.value })
  //         }
  //         MenuProps={{
  //           PaperProps: {
  //             sx: {
  //               bgcolor: "#1A1A1A",
  //               border: "1px solid rgba(255, 239, 153, 0.20)",
  //               "& .MuiMenuItem-root": {
  //                 color: "#FFF",
  //                 "&:hover": {
  //                   bgcolor: "rgba(255, 215, 0, 0.15)",
  //                 },
  //                 "&.Mui-selected": {
  //                   bgcolor: "rgba(255, 215, 0, 0.1)",
  //                   "&:hover": {
  //                     bgcolor: "rgba(255, 215, 0, 0.15)",
  //                   },
  //                 },
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="">All</MenuItem>
  //         {categoryChoices.map((choice) => (
  //           <MenuItem key={choice.id} value={choice.id}>
  //             {choice.name}
  //           </MenuItem>
  //         ))}
  //       </Select>
  //     </FormControl>

  //     <FormControl
  //       size="small"
  //       sx={{
  //         minWidth: 150,
  //         "& .MuiInputLabel-root": {
  //           color: "rgba(255, 255, 255, 0.7)",
  //           "&.Mui-focused": {
  //             color: "#FFD700",
  //           },
  //         },
  //         "& .MuiOutlinedInput-root": {
  //           color: "#FFF",
  //           bgcolor: "rgba(255, 255, 255, 0.05)",
  //           "& fieldset": {
  //             borderColor: "rgba(255, 239, 153, 0.20)",
  //           },
  //           "&:hover fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //           "&.Mui-focused fieldset": {
  //             borderColor: "#FFD700",
  //           },
  //         },
  //         "& .MuiSelect-icon": {
  //           color: "#FFD700",
  //         },
  //       }}
  //     >
  //       <InputLabel>Status</InputLabel>
  //       <Select
  //         value={selectedStatus}
  //         label="Status"
  //         onChange={(e) =>
  //           setFilters({ ...filterValues, status: e.target.value })
  //         }
  //         MenuProps={{
  //           PaperProps: {
  //             sx: {
  //               bgcolor: "#1A1A1A",
  //               border: "1px solid rgba(255, 239, 153, 0.20)",
  //               "& .MuiMenuItem-root": {
  //                 color: "#FFF",
  //                 "&:hover": {
  //                   bgcolor: "rgba(255, 215, 0, 0.15)",
  //                 },
  //                 "&.Mui-selected": {
  //                   bgcolor: "rgba(255, 215, 0, 0.1)",
  //                   "&:hover": {
  //                     bgcolor: "rgba(255, 215, 0, 0.15)",
  //                   },
  //                 },
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="">All</MenuItem>
  //         {statusChoices.map((choice) => (
  //           <MenuItem key={choice.id} value={choice.id}>
  //             {choice.name}
  //           </MenuItem>
  //         ))}
  //       </Select>
  //     </FormControl>
  //   </Box>,
  // ];

  const filters = [
    <Box
      key="search-filter"
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        width: "100%",
        mb: 0.5,
        "@media (max-width:536px)": {
          gap: 1,
        },
      }}
      alwaysOn
    >
      {/* Search input and buttons - First row on mobile, part of single row on desktop */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          "@media (max-width:536px)": {
            width: "100%",
            gap: 1,
          },
          "@media (min-width:537px)": {
            flex: "0 0 auto",
            flexWrap: "nowrap",
          },
        }}
      >
        <MuiTextField
          size="small"
          variant="outlined"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          placeholder="Username or TicketId"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
          sx={{
            minWidth: "150px",
            "@media (min-width:537px)": {
              minWidth: "200px",
            },
            "@media (max-width:536px)": {
              flex: 1,
            },
            marginTop: "0 !important",
            "& .MuiInputBase-root": {
              color: "#FFF",
              bgcolor: "rgba(255, 255, 255, 0.05)",
              "& fieldset": {
                borderColor: "rgba(255, 239, 153, 0.20)",
              },
              "&:hover fieldset": {
                borderColor: "#FFD700",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FFD700",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255, 255, 255, 0.5)",
            },
          }}
        />
        <Button
          variant="contained"
          sx={{
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={handleApply}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          sx={{
            border: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
            flexShrink: 0,
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>

      {/* Category and Status dropdowns - Second row on mobile, part of single row on desktop */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          flexWrap: "wrap",
          "@media (max-width:536px)": {
            width: "100%",
            gap: 1,
          },
          "@media (min-width:537px)": {
            flex: "0 0 auto",
            flexWrap: "nowrap",
          },
        }}
      >
        <FormControl
          size="small"
          sx={{
            minWidth: "150px",
            flex: "1 1 auto",
            "@media (max-width:536px)": {
              minWidth: "calc(50% - 4px)",
              flex: "1 1 calc(50% - 4px)",
            },
            "& .MuiInputLabel-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-focused": {
                color: "#FFD700",
              },
            },
            "& .MuiOutlinedInput-root": {
              color: "#FFF",
              bgcolor: "rgba(255, 255, 255, 0.05)",
              "& fieldset": {
                borderColor: "rgba(255, 239, 153, 0.20)",
              },
              "&:hover fieldset": {
                borderColor: "#FFD700",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FFD700",
              },
            },
            "& .MuiSelect-icon": {
              color: "#FFD700",
            },
          }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) =>
              setFilters({ ...filterValues, category: e.target.value })
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: "#1A1A1A",
                  border: "1px solid rgba(255, 239, 153, 0.20)",
                  "& .MuiMenuItem-root": {
                    color: "#FFF",
                    "&:hover": {
                      bgcolor: "rgba(255, 215, 0, 0.15)",
                    },
                    "&.Mui-selected": {
                      bgcolor: "rgba(255, 215, 0, 0.1)",
                      "&:hover": {
                        bgcolor: "rgba(255, 215, 0, 0.15)",
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {categoryChoices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            minWidth: "150px",
            flex: "1 1 auto",
            "@media (max-width:536px)": {
              minWidth: "calc(50% - 4px)",
              flex: "1 1 calc(50% - 4px)",
            },
            "& .MuiInputLabel-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-focused": {
                color: "#FFD700",
              },
            },
            "& .MuiOutlinedInput-root": {
              color: "#FFF",
              bgcolor: "rgba(255, 255, 255, 0.05)",
              "& fieldset": {
                borderColor: "rgba(255, 239, 153, 0.20)",
              },
              "&:hover fieldset": {
                borderColor: "#FFD700",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FFD700",
              },
            },
            "& .MuiSelect-icon": {
              color: "#FFD700",
            },
          }}
        >
          <InputLabel>Role</InputLabel>
          <Select
            value={selectedRole}
            label="Status"
            onChange={(e) =>
              setFilters({ ...filterValues, role: e.target.value })
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: "#1A1A1A",
                  border: "1px solid rgba(255, 239, 153, 0.20)",
                  "& .MuiMenuItem-root": {
                    color: "#FFF",
                    "&:hover": {
                      bgcolor: "rgba(255, 215, 0, 0.15)",
                    },
                    "&.Mui-selected": {
                      bgcolor: "rgba(255, 215, 0, 0.1)",
                      "&:hover": {
                        bgcolor: "rgba(255, 215, 0, 0.15)",
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {roleChoices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{
            minWidth: "150px",
            flex: "1 1 auto",
            "@media (max-width:536px)": {
              minWidth: "calc(50% - 4px)",
              flex: "1 1 calc(50% - 4px)",
            },
            "& .MuiInputLabel-root": {
              color: "rgba(255, 255, 255, 0.7)",
              "&.Mui-focused": {
                color: "#FFD700",
              },
            },
            "& .MuiOutlinedInput-root": {
              color: "#FFF",
              bgcolor: "rgba(255, 255, 255, 0.05)",
              "& fieldset": {
                borderColor: "rgba(255, 239, 153, 0.20)",
              },
              "&:hover fieldset": {
                borderColor: "#FFD700",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FFD700",
              },
            },
            "& .MuiSelect-icon": {
              color: "#FFD700",
            },
          }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={selectedStatus}
            label="Status"
            onChange={(e) =>
              setFilters({ ...filterValues, status: e.target.value })
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: "#1A1A1A",
                  border: "1px solid rgba(255, 239, 153, 0.20)",
                  "& .MuiMenuItem-root": {
                    color: "#FFF",
                    "&:hover": {
                      bgcolor: "rgba(255, 215, 0, 0.15)",
                    },
                    "&.Mui-selected": {
                      bgcolor: "rgba(255, 215, 0, 0.1)",
                      "&:hover": {
                        bgcolor: "rgba(255, 215, 0, 0.15)",
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {statusChoices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>,
  ];
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 80px)",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 2 }}>
          <Box
            sx={{
              mt: 2,
              backgroundColor: "#242424",
              borderRadius: 2,
              boxShadow: "none !important",
              padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
            }}
          >
            <List
              {...props}
              filters={filters}
              actions={postListActions}
              sort={{ field: "createdAt", order: "DESC" }}
              pagination={false}
              sx={{
                "& .RaList-actions": {
                  flexWrap: "nowrap",
                },
                "& .css-1tgsrh5-RaListToolbar-root": {
                  "@media (min-width:537px)": {
                    marginTop: "0 !important",
                  },
                  "@media (max-width:536px)": {
                    paddingRight: "10px",
                  },
                },
                "& .RaFilterFormInput-spacer": { display: "none" },
              }}
            >
              <Box
                style={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Box
                  style={{
                    width: "100%",
                    position: isSmallScreen ? "absolute" : "relative",
                  }}
                >
                  <Datagrid
                    size="small"
                    bulkActionButtons={false}
                    sx={{
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "100%",
                      maxHeight: "100%",
                      "& .RaDatagrid-row": {
                        borderBottom: "1px solid rgba(255, 239, 153, 0.10)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 215, 0, 0.05)",
                        },
                      },
                      "& .RaDatagrid-header": {
                        backgroundColor: "rgba(255, 215, 0, 0.05)",
                        fontWeight: 600,
                        borderBottom: "2px solid rgba(255, 239, 153, 0.20)",
                      },
                      "& .MuiTableCell-head": {
                        fontWeight: 600,
                        color: "#FFD700",
                      },
                      "& .MuiTableCell-body": {
                        color: "#FFF",
                      },
                      borderRadius: "8px",
                      border: "1px solid rgba(255, 239, 153, 0.20)",
                    }}
                  >
                    <WrapperField label="Actions">
                      <TicketActions
                        onEdit={handleEditTicket}
                        onViewDetails={handleViewDetails}
                      />
                    </WrapperField>
                    <TextField source="username" label="Username" />
                    <TextField source="role" label="Role" />
                    <TextField label="Ticket ID" source="id" />
                    <FunctionField
                      label="Category"
                      render={(record) =>
                        capitalizeFirstLetter(record.category)
                      }
                    />
                    <FunctionField
                      label="Status"
                      render={(record) => (
                        <Chip
                          label={capitalizeFirstLetter(record.status)}
                          color={getStatusColor(record.status)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        />
                      )}
                    />
                    <DateField
                      source="createdAt"
                      label="Date & Time"
                      showTime
                    />
                  </Datagrid>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100% !important",
                      margin: "16px 0px",
                    }}
                  >
                    <CustomPagination
                      page={page}
                      perPage={perPage}
                      total={total}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </Box>
                </Box>
              </Box>
            </List>
          </Box>
        </Box>
      </Box>

      {/* Edit Ticket Dialog */}
      <EditTicketDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        ticket={selectedTicket}
        onSuccess={handleEditSuccess}
      />

      {/* Ticket Details Dialog */}
      <TicketDetailsDialog
        open={detailsDialogOpen}
        onClose={handleDetailsClose}
        ticket={selectedTicket}
      />
    </>
  );
};

export default TicketsList;
